import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Snackbar,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import Editor from '@monaco-editor/react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface PineScriptEditorProps {
  initialValue?: string;
  onChange?: (code: string) => void;
  onSave?: (code: string) => void;
  onTest?: (code: string) => void;
  height?: string;
  readOnly?: boolean;
  onEditorReady?: (getCode: () => string) => void;
}

export const PineScriptEditor: React.FC<PineScriptEditorProps> = ({
  initialValue = getDefaultPineScript(),
  onChange,
  onSave,
  onTest,
  height = '500px',
  readOnly = false,
  onEditorReady
}) => {
  const [code, setCode] = useState<string>(initialValue);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  
  // Reference to the editor instance
  const editorRef = useRef<any>(null);
  
  // Function to get the current code
  const getCode = () => {
    return code;
  };

  // Function to handle editor mounting
  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;
    
    // Scroll to the bottom of the editor after it mounts
    setTimeout(() => {
      if (editorRef.current) {
        const lineCount = editorRef.current.getModel().getLineCount();
        editorRef.current.revealLine(lineCount);
      }
    }, 100);
    
    // Notify parent component that editor is ready
    if (onEditorReady) {
      onEditorReady(getCode);
    }
  };

  useEffect(() => {
    // Update code if initialValue changes externally
    if (initialValue !== code) {
      setCode(initialValue);
      
      // Scroll to bottom after initialValue changes
      setTimeout(() => {
        if (editorRef.current) {
          const lineCount = editorRef.current.getModel().getLineCount();
          editorRef.current.revealLine(lineCount);
          // Place cursor at the end
          const lastLineLength = editorRef.current.getModel().getLineContent(lineCount).length;
          editorRef.current.setPosition({ lineNumber: lineCount, column: lastLineLength + 1 });
        }
      }, 100);
    }
  }, [initialValue]);

  const handleEditorChange = (value: string | undefined) => {
    const newCode = value || '';
    setCode(newCode);
    if (onChange) {
      onChange(newCode);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(code);
    }
    setNotification({
      open: true,
      message: 'PineScript saved successfully!',
      type: 'success'
    });
  };

  const handleTest = () => {
    if (onTest) {
      onTest(code);
    } else {
      setNotification({
        open: true,
        message: 'Test functionality not provided',
        type: 'info'
      });
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code);
    setNotification({
      open: true,
      message: 'Code copied to clipboard!',
      type: 'success'
    });
  };

  const handleOpenTradingView = () => {
    window.open('https://www.tradingview.com/pine-script-editor/', '_blank');
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  return (
    <Paper elevation={3} sx={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6">PineScript Editor</Typography>
        <Box>
          <Tooltip title="Copy code">
            <IconButton onClick={handleCopyCode}>
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Save">
            <IconButton onClick={handleSave} disabled={readOnly}>
              <SaveIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Test on TradingView">
            <IconButton onClick={handleTest}>
              <PlayArrowIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open in TradingView">
            <IconButton onClick={handleOpenTradingView}>
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Editor
          height={height}
          defaultLanguage="typescript" // Use TypeScript for syntax highlighting (closest to PineScript)
          defaultValue={code}
          value={code}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          options={{
            minimap: { enabled: true },
            scrollBeyondLastLine: true, // Allow scrolling beyond last line to improve visibility
            fontSize: 14,
            readOnly: readOnly,
            lineNumbers: 'on',
            wordWrap: 'on',
            automaticLayout: true, // Automatically adjust layout on resize
            scrollbar: {
              vertical: 'visible',
              horizontalSliderSize: 10,
              verticalSliderSize: 10
            }
          }}
        />
      </Box>

      {notification && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.type}
            variant="filled"
          >
            {notification.message}
          </Alert>
        </Snackbar>
      )}
    </Paper>
  );
};

// Helper function to provide default PineScript template
function getDefaultPineScript(): string {
  return `//@version=5
strategy("My Trading Strategy", overlay=true)

// Input parameters
fastLength = input(12, title="Fast Length")
slowLength = input(26, title="Slow Length")
signalLength = input(9, title="Signal Length")

// Calculate indicators
[macdLine, signalLine, _] = ta.macd(close, fastLength, slowLength, signalLength)

// Strategy logic
if (ta.crossover(macdLine, signalLine))
    strategy.entry("Buy", strategy.long)

if (ta.crossunder(macdLine, signalLine))
    strategy.close("Buy")

// Plot indicators
plot(macdLine, color=color.blue)
plot(signalLine, color=color.red)
`;
}

export default PineScriptEditor;
